import { useState } from 'react';
import { Camera, Upload, Globe, CheckCircle, ArrowRight } from 'lucide-react';
import Button from '../components/Button';
import { countries } from '../constants/countries';

interface HomePageProps {
  onNavigate: (page: string) => void;
}

export default function HomePage({ onNavigate }: HomePageProps) {
  const [showAllCountries, setShowAllCountries] = useState(false);
  const displayedCountries = showAllCountries ? countries : countries.slice(0, 6);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className="pt-20 pb-16 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Professional Passport Photos in Minutes
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Generate compliant passport photos for any country. Fast, easy, and guaranteed to meet official requirements.
            </p>
            <Button
              onClick={() => onNavigate('upload')}
              size="lg"
              icon={<ArrowRight className="w-5 h-5" />}
            >
              Start Now
            </Button>
          </div>

          <div className="mt-16 relative">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl mx-auto border border-gray-200">
              <div className="aspect-video bg-gradient-to-br from-blue-100 to-blue-50 rounded-lg flex items-center justify-center">
                <Camera className="w-24 h-24 text-blue-400" />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Three simple steps to your perfect passport photo
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Upload className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Upload Your Photo</h3>
              <p className="text-gray-600">
                Take or upload a photo with a plain background. Our system will guide you through the requirements.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Globe className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">2. Select Your Country</h3>
              <p className="text-gray-600">
                Choose from {countries.length} countries. We automatically apply the correct format and specifications.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3. Download & Use</h3>
              <p className="text-gray-600">
                Get your professionally formatted photo instantly. Print or use digitally for your application.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Popular Formats
            </h2>
            <p className="text-lg text-gray-600">
              We support official requirements for countries worldwide
            </p>
          </div>

          <div className={`grid gap-4 ${
            showAllCountries
              ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
              : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
          }`}>
            {displayedCountries.map((country) => (
              <button
                key={country.code}
                onClick={() => onNavigate('upload')}
                className="bg-white rounded-lg p-6 shadow-md border border-gray-200 hover:shadow-lg hover:border-blue-300 transition-all group"
              >
                <div className="text-4xl mb-3">{country.flag}</div>
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {country.name}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {country.dimensions.width} × {country.dimensions.height} {country.dimensions.unit}
                </p>
              </button>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button
              onClick={() => setShowAllCountries(!showAllCountries)}
              variant="outline"
            >
              {showAllCountries ? 'Show Less' : 'See All Countries'}
            </Button>
          </div>
        </section>

        <section className="py-16 bg-blue-600 rounded-2xl my-16 px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-white">
            Create your compliant passport photo in less than 5 minutes
          </p>
          <Button
            onClick={() => onNavigate('upload')}
            variant="secondary"
            size="lg"
            icon={<Camera className="w-5 h-5" />}
          >
            Create Your Photo Now
          </Button>
        </section>
      </div>
    </div>
  );
}
